/*
 * Copyright © 2018-2026, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { strings } from "../../../../common/Strings/Strings";
import { getAddressString, getGeneralError } from "../../../../util/helperFunctions";
import { ReservationTypeResponse } from "../../../../models/reservationtype/ReservationTypeResponse";
import ClinicApi from "../../../../api/ClinicApi";
import logger from "../../../../util/logger";
import LoaderInline from "../../../../components/LoaderInline";
import AlertBox, { AlertType } from "../../../../components/AlertBox";
import { useUser } from "../../../../contexts/UserContext";
import { PageProps } from "../../../../models/PageProps";
import { Loader } from "../../../../components/Loader";
import { BasicClinicResponse } from "../../../../models/clinic/BasicClinicResponse";
import { ClinicProfileResponse } from "../../../../models/clinic/profile/ClinicProfileResponse";
import ClinicProfileApi from "../../../../api/ClinicProfileApi";
import ClinicProfile from "../../../Clinic/Profile/ClinicProfile";
import EmptyListText from "../../../../components/EmptyListText";
import { PetDetailsResponse } from "../../../../models/pet/PetDetailsResponse";
import UserPetApi from "../../../../api/UserPetApi";
import ReservationApi from "../../../../api/ReservationApi";
import Button from "../../../../components/Button";
import ClinicLogo from "../../../../components/Pictures/Clinic/ClinicLogo";
import LocationFilter from "./LocationFilter";

interface ReservationTypeForm {
  selectedClinics: BasicClinicResponse[];
  resType: ReservationTypeResponse | undefined;
  videoConsultation: "inPerson" | "video";
}

type Params = {
  userPetId?: string;
};

const ChooseClinic: React.FC<PageProps> = ({ setPageLoading }: PageProps) => {
  const { user } = useUser();
  const [clinics, setClinics] = useState<BasicClinicResponse[]>([]);

  const [error, setError] = useState<string | null>(null);
  const { userPetId } = useParams<Params>();

  const { control } = useForm<ReservationTypeForm>({
    mode: "onChange",
    defaultValues: { videoConsultation: "inPerson" },
  });
  const [profile, setProfile] = useState<ClinicProfileResponse>();
  const [profileLoading, setProfileLoading] = useState<boolean>(false);
  const [pets, setPets] = useState<PetDetailsResponse[]>();
  const [petsLoading, setPetsLoading] = useState<boolean>(false);
  const [visitedClinics, setVisitedClinics] = useState<BasicClinicResponse[]>([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState<boolean>(false);

  useEffect(() => {
    const getClinics = async () => {
      try {
        const response = await ClinicApi.getClinics({
          availableForReservation: true,
        });
        setClinics(response.data);
      } catch (err) {
        setError(await getGeneralError(err));
        logger.error(err);
      } finally {
        setPageLoading(false);
      }
    };

    void getClinics();
  }, [setPageLoading]);

  useEffect(() => {
    const getAppointments = async () => {
      setAppointmentsLoading(true);
      try {
        const response = await ReservationApi.getUserReservations();
        const clinicIdsFromAppointments = new Set<string>([]);

        response.data.forEach((r) => {
          clinicIdsFromAppointments.add(r.clinicId);
        });

        const clinicsFromAppointments: Array<BasicClinicResponse> = [];
        Array.from(clinicIdsFromAppointments).forEach((cid) => {
          const tempClinic = clinics.find((c) => c.id === cid);
          if (tempClinic) {
            clinicsFromAppointments.push(tempClinic);
          }
        });
        setVisitedClinics(clinicsFromAppointments);
      } catch (e) {
        logger.error(e);
      } finally {
        setAppointmentsLoading(false);
      }
    };
    if (clinics.length > 0) {
      void getAppointments();
    }
  }, [clinics]);

  useEffect(() => {
    const getMyPets = async () => {
      setPetsLoading(true);
      try {
        const response = await UserPetApi.getUserPets();
        setPets(response.data.map((p) => p.userPetDetails));
      } catch (err) {
        logger.error(err);
      } finally {
        setPetsLoading(false);
      }
    };

    if (!appointmentsLoading && visitedClinics && visitedClinics.length === 0) {
      void getMyPets();
    }
  }, [visitedClinics, appointmentsLoading]);

  const getProfile = async (id: string) => {
    setProfileLoading(true);
    try {
      const response = await ClinicProfileApi.getClinicProfile(id);
      setProfile(response.data);
    } catch (e) {
      setError(await getGeneralError(e));
      logger.error(e);
    } finally {
      setProfileLoading(false);
    }
  };

  const selectClinic = (clinic: BasicClinicResponse) => {
    if (clinic) {
      void getProfile(clinic.id);
    }
  };

  return (
    <main className="main-signed-in">
      <section>
        <div className="flex flex-col items-center justify-center space-y-6 mx-auto px-4 md:px-0 py-10 md:py-14">
          <div className="w-full md:max-w-lg md:tw-card space-y-6 md:p-8">
            <div className="flex justify-between space-x-4">
              <div>
                <h1 className="text-xl font-semibold leading-tight text-zinc-800 lg:text-2xl dark:text-white">
                  {strings.addAppointment}
                </h1>
              </div>
              <span className="text-gray-500 dark:text-gray-400">{profile ? 2 : 1}/4</span>
            </div>
            <AlertBox
              hidden={!!user.primaryAddress}
              message={
                <>
                  <div>{strings.warningUserHasNoPrimaryAddress}</div>
                  <Link to="/profile-settings">{strings.warningUserHasNoPrimaryAddressLinkToProfile}</Link>
                </>
              }
              type={AlertType.INFO}
            />
            <AlertBox
              hidden={petsLoading || !pets || (pets && pets?.length > 0)}
              message={
                <>
                  <div>{strings.appointmentErrorNoPet}</div>
                  <Link to="/add-new-pet">{strings.addFirstPetButton}</Link>
                </>
              }
              type={AlertType.INFO}
            />
            {profileLoading ? <LoaderInline /> : <></>}
            {profile && !profileLoading ? (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold leading-tight text-zinc-800 lg:text-xl dark:text-white">
                  {strings.newAppointmentSubTitle2}
                </h2>
                <div className="space-y-6">
                  <ClinicProfile isSmall profile={profile} showSettingsOption={false} userPetId={userPetId} />
                </div>
                <Button
                  onClick={() => {
                    if (profile) {
                      setProfile(undefined);
                    }
                  }}
                  variant="link"
                >
                  {strings.back}
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold leading-tight text-zinc-800 lg:text-xl dark:text-white">
                  {strings.newAppointmentSubTitle1}
                </h2>
                <form className="w-full space-y-6">
                  <AlertBox message={error} />
                  <div>
                    {visitedClinics.length > 0 ? (
                      <div className="space-y-4">
                        {visitedClinics.map((visitedClinic) => (
                          <div className="tw-card flex p-4 space-x-4 items-center">
                            <div className="rounded-full border-2 border-gray-100 w-14 h-14 flex-shrink-0 bg-white bg-no-repeat bg-cover bg-center dark:border-gray-800 dark:bg-gray-700">
                              <ClinicLogo clinicId={visitedClinic?.id} logoPictureId={visitedClinic?.logoPictureId} />
                            </div>
                            <div>
                              <Button
                                className="tw-link-dark"
                                onClick={() => {
                                  selectClinic(visitedClinic);
                                }}
                                variant="link"
                              >
                                {visitedClinic.name}
                              </Button>
                              <div>{visitedClinic?.address && getAddressString(visitedClinic.address)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <EmptyListText text={strings.noClinicsVisitedYet} />
                    )}
                  </div>
                  <div className="flex items-center justify-center">
                    <span className="w-full border border-gray-200 dark:border-gray-700" />
                    <span className="px-4 text-gray-500 dark:text-gray-400">{strings.OR}</span>
                    <span className="w-full border border-gray-200 dark:border-gray-700" />
                  </div>
                  <LocationFilter clinics={clinics} control={control} selectClinic={selectClinic} />
                </form>
              </div>
            )}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Loader(ChooseClinic);
